import { useCallback, useState } from 'react';

export type RegistrationRole = 'customer' | 'carrier';
export type RegistrationAccountType = '' | 'person' | 'company';
export type CarrierEntityType = '' | 'person' | 'sole_proprietor' | 'company';

const COUNTRY_CODES = [
  '370', '371', '372', '373', '374', '375', '376', '377', '378', '379',
  '380', '381', '382', '383', '385', '386', '387', '389',
  '420', '421', '423',
  '992', '993', '994', '995', '996', '998',
  '971', '972', '973', '974', '975', '976', '977',
  '350', '351', '352', '353', '354', '355', '356', '357', '358', '359',
  '20', '27', '30', '31', '32', '33', '34', '36', '39',
  '40', '41', '43', '44', '45', '46', '47', '48', '49',
  '90', '98',
  '7', '1',
];

export function maskPhone(value: string | null | undefined): string {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 4) return value;

  let normalized = digits;
  if (digits.length === 11 && digits.startsWith('8')) {
    normalized = `7${digits.slice(1)}`;
  }

  let matchedCode = '';
  for (const code of COUNTRY_CODES) {
    if (normalized.startsWith(code)) {
      matchedCode = code;
      break;
    }
  }

  const countryCode = matchedCode || (normalized.length > 10 ? normalized.slice(0, 3) : normalized.length > 8 ? normalized.slice(0, 2) : normalized.slice(0, 1));
  const national = normalized.slice(countryCode.length);

  // 1. Lithuania / Baltics (8 digits national: +370 6**-**-*93)
  if (['370', '371', '372'].includes(countryCode) && national.length === 8) {
    return `+${countryCode} ${national.slice(0, 1)}**-**-*${national.slice(-2)}`;
  }

  // 2. Russia / Kazakhstan / CIS (10 digits national: +7 916-***-**-77)
  if (national.length === 10) {
    return `+${countryCode} ${national.slice(0, 3)}-***-**-${national.slice(-2)}`;
  }

  // 3. 9 digits national: +48 51-**-**-93
  if (national.length === 9) {
    const openNational = Math.max(1, 4 - countryCode.length);
    return `+${countryCode} ${national.slice(0, openNational)}-**-**-${national.slice(-2)}`;
  }

  // 4. Fallback
  const openNational = Math.max(1, 4 - countryCode.length);
  const prefix = national.slice(0, openNational);
  const suffix = national.slice(-2);
  const hiddenCount = Math.max(1, national.length - openNational - 2);
  return `+${countryCode} ${prefix}-${'*'.repeat(hiddenCount)}-${suffix}`;
}

export interface RegistrationDraft {
  targetRole: RegistrationRole;
  email: string;
  accountType: RegistrationAccountType;
  carrierEntityType: CarrierEntityType;
  firstName: string;
  lastName: string;
  companyName: string;
  contactName: string;
  phone: string;
  smsCode: string;
  debugSmsCode: string;
}

const STORAGE_KEY = 'lovo:v2:registration-draft';

const EMPTY_DRAFT: RegistrationDraft = {
  targetRole: 'customer',
  email: '',
  accountType: '',
  carrierEntityType: '',
  firstName: '',
  lastName: '',
  companyName: '',
  contactName: '',
  phone: '',
  smsCode: '',
  debugSmsCode: '',
};

function readDraft(): RegistrationDraft {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_DRAFT;
    return { ...EMPTY_DRAFT, ...(JSON.parse(raw) as Partial<RegistrationDraft>) };
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return EMPTY_DRAFT;
  }
}

export function setRegistrationTargetRole(targetRole: RegistrationRole) {
  const current = readDraft();
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, targetRole }));
}

export function useRegistrationDraft() {
  const [draft, setDraft] = useState<RegistrationDraft>(readDraft);

  const updateDraft = useCallback((patch: Partial<RegistrationDraft>) => {
    setDraft((current) => {
      const next = { ...current, ...patch };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setDraft(EMPTY_DRAFT);
  }, []);

  return { clearDraft, draft, updateDraft };
}
